import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import ascflowIcon from "@/assets/ascflow-icon.png";

interface CobrancaWhatsAppMockupProps {
  onClose?: () => void;
  onComplete?: () => void;
}

const CobrancaWhatsAppMockup = ({
  onClose,
  onComplete,
}: CobrancaWhatsAppMockupProps) => {
  const [loadingText, setLoadingText] = useState("");
  const [showLoading, setShowLoading] = useState(true);
  const [finalText, setFinalText] = useState("");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const loadingMessage = "Buscando os dados fornecidos pela sua empresa";
  const fullText =
    "Esta é uma demonstração de uso do nosso utilitário automatizado de cobrança. Buscaremos através da sua database e faremos a cobrança automaticamente de acordo com suas demandas.";

  useEffect(() => {
    // First: Loading animation
    let currentIndex = 0;
    const loadingInterval = setInterval(() => {
      if (currentIndex <= loadingMessage.length) {
        setLoadingText(loadingMessage.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(loadingInterval);
        // Wait 1 second then show final message
        setTimeout(() => {
          setShowLoading(false);
          if (onComplete) onComplete();
          // Start typing final message
          let finalIndex = 0;
          const finalInterval = setInterval(() => {
            if (finalIndex <= fullText.length) {
              setFinalText(fullText.slice(0, finalIndex));
              finalIndex++;
            } else {
              clearInterval(finalInterval);
              // sinaliza para o pai que o processo terminou somente APÓS a digitação completa
              if (onComplete) onComplete();
            }
          }, 30);
        }, 1000);
      }
    }, 50);

    return () => clearInterval(loadingInterval);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    setIsDragging(true);
    const touch = e.touches[0];
    dragStart.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.current.x,
      y: touch.clientY - dragStart.current.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // mouseup global (caso solte fora do componente)
  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener("mouseup", handleGlobalMouseUp);
    return () => window.removeEventListener("mouseup", handleGlobalMouseUp);
  }, []);

  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textBubbleRef = useRef<HTMLDivElement>(null);

  // Scroll automático (programático) rigoroso para mobile
  useEffect(() => {
    if (chatContainerRef.current && window.innerWidth < 768) {
      if (showLoading) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      } else if (textBubbleRef.current) {
        // Trava a rolagem no fim do balão de texto, impedindo que desça para o footer
        const bubbleBottom = textBubbleRef.current.offsetTop + textBubbleRef.current.offsetHeight;
        const containerHeight = chatContainerRef.current.clientHeight;

        // Define o scroll exato para mostrar o texto + 20px de respiro
        chatContainerRef.current.scrollTop = bubbleBottom - containerHeight + 20;
      }
    }
  }, [loadingText, finalText, showLoading]);

  const [stylePos, setStylePos] = useState({ bottom: "80px", right: "80px" });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setStylePos({ bottom: "20px", right: "20px" });
      } else {
        setStylePos({ bottom: "80px", right: "80px" });
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      className="absolute w-[130px] md:w-[240px] h-[185px] md:h-[340px] bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-2xl overflow-hidden flex flex-col z-[10005] font-sans border-2 border-purple-200/50"
      style={{
        bottom: stylePos.bottom,
        right: stylePos.right,
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: isDragging ? "grabbing" : "auto",
        backgroundColor: "#0b141a",
      }}
      onMouseMove={handleMouseMove}
    >
      {/* Cabeçalho - Área Arrastável */}
      <div
        className="bg-gradient-to-r from-purple-600 to-purple-700 p-1.5 md:p-2.5 flex items-center justify-between text-white shadow-md z-10 cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex items-center gap-2 pointer-events-none">
          {/* Avatar AscFlow */}
          <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-white flex items-center justify-center overflow-hidden">
            <img
              src={ascflowIcon}
              alt="AscFlow"
              className="w-5 h-5 md:w-7 md:h-7 object-contain"
            />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-[10px] md:text-sm leading-tight">
              Sistema de Cobrança
            </span>
            <span className="text-[8px] md:text-[10px] text-white/80">
              {showLoading ? "processando..." : "concluído ✓"}
            </span>
          </div>
        </div>
        <div className="flex gap-2 text-white/90 pointer-events-none">
          <button
            onClick={handleClose}
            className="pointer-events-auto hover:bg-white/20 rounded p-0.5 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Área de Chat */}
      <div
        ref={chatContainerRef}
        className="flex-1 p-3 overflow-hidden relative"
        style={{
          backgroundImage:
            'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundColor: "#0b141a",
        }}
      >
        {/* Balão de Processamento (só aparece durante loading) */}
        {showLoading && (
          <div className="bg-[#202c33] p-2 md:p-2.5 rounded-lg rounded-tl-none shadow-sm max-w-[90%] self-start text-[8px] md:text-xs text-[#e9edef] relative leading-relaxed border-l-4 border-purple-500">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="font-semibold text-purple-500">Processando</span>
            </div>
            {loadingText}
            <span className="inline-block w-[2px] h-3 bg-purple-500 ml-1 animate-pulse align-middle"></span>
            <span className="text-[9px] text-gray-400 block text-right mt-1">
              {new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            {/* Cauda do balão */}
            <div className="absolute top-0 left-[-8px] w-0 h-0 border-t-[10px] border-t-[#202c33] border-l-[10px] border-l-transparent"></div>
          </div>
        )}

        {/* Balão de Conclusão (aparece após loading terminar e permanece) */}
        {!showLoading && (
          <>
            {/* Balão de Conclusão (aparece após loading terminar e permanece) */}
            <div
              ref={textBubbleRef}
              className="bg-[#202c33] p-2 md:p-2.5 rounded-lg rounded-tl-none shadow-sm max-w-[90%] self-start text-[8px] md:text-xs text-[#e9edef] relative leading-relaxed border-l-4 border-purple-500"
            >
              <div className="flex items-center gap-1 mb-1">
                <span className="font-semibold text-red-500">✓ Concluído</span>
              </div>
              {finalText}
              <span className="inline-block w-[2px] h-3 bg-red-500 ml-1 animate-pulse align-middle"></span>
              <span className="text-[9px] text-gray-400 block text-right mt-1">
                {new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              {/* Cauda do balão */}
              <div className="absolute top-0 left-[-8px] w-0 h-0 border-t-[10px] border-t-[#202c33] border-l-[10px] border-l-transparent"></div>
            </div>

            {/* SVG da apresentação, exibido após concluir */}
            <div className="mt-3 w-full flex justify-center [&>svg]:w-full [&>svg]:h-auto">
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: `
<svg viewBox="0 0 687 491" style="width: 100%; height: auto;" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
<rect width="687" height="491" fill="url(#pattern0_6_2)"/>
<defs>
<pattern id="pattern0_6_2" patternContentUnits="objectBoundingBox" width="1" height="1">
<use xlink:href="#image0_6_2" transform="scale(0.000250071 0.000349895)"/>
</pattern>
<image id="image0_6_2" width="4000" height="2858" preserveAspectRatio="none" xlink:href="data:image/jpeg;base64,/* ... sua imagem gigante aqui ... */"/>
</defs>
</svg>
                `,
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Área de Entrada */}
      <div className="bg-[#F0F2F5] p-1 md:p-2 flex items-center gap-2">
        <div className="bg-white rounded-full flex-1 px-2 py-1 md:px-3 md:py-1.5 text-[8px] md:text-xs text-gray-400 shadow-sm border border-gray-200">
          <span>Sistema automatizado</span>
        </div>
      </div>
    </div>
  );
};

export default CobrancaWhatsAppMockup;
